import { any, bool, func, object, string } from "prop-types";
import React from "react";

import TextField from "@components/shared/TextField";
import SelectField from "@components/shared/SelectField";

import { useTranslation } from "@lib/i18n";
import { useAddressPostCodePattern } from "@hooks/useAddressPostCodePattern";
import {
  useCountriesSelector,
  useCountrySelectionConfigSelector,
} from "@redux/reducers/appConfig";

import {
  GUEST_CHECKOUT_ADDRESS_PREFIXES,
  USER_FIELDS_NAMES,
} from "@shared/constants";

const { ADDRESS, GUEST_CHECKOUT_ADDRESS } = USER_FIELDS_NAMES;

export const CustomerCreationFormAddress = props => {
  const { t } = useTranslation();
  const {
    isProvinceRelevantForCountry,
    prefix,
    regexPatterns,
    register,
    required = true,
    watch,
  } = props;

  const countries = useCountriesSelector();
  const {
    enabled: isCountrySelectEnabled,
  } = useCountrySelectionConfigSelector();

  const POST_CODE =
    prefix === GUEST_CHECKOUT_ADDRESS_PREFIXES.REGISTRATION
      ? GUEST_CHECKOUT_ADDRESS.POSTAL_CODE
      : GUEST_CHECKOUT_ADDRESS.POST_CODE;

  const COUNTRY_FIELD = `${prefix}${GUEST_CHECKOUT_ADDRESS.COUNTRY}`;

  const isCountrySelectVisible = isCountrySelectEnabled && countries.length > 0;

  const selectedCountry =
    isCountrySelectVisible && watch ? watch(COUNTRY_FIELD) : null;
  const postCodePattern = useAddressPostCodePattern(selectedCountry);

  return (
    <>
      <TextField
        errorHelperMessage={t(
          `myDetails.helperTexts.${ADDRESS.PRIMARY_ADDRESS}`
        )}
        fullWidth
        label={t(`myDetails.inputs.${ADDRESS.PRIMARY_ADDRESS}`)}
        name={`${prefix}${GUEST_CHECKOUT_ADDRESS.PRIMARY_ADDRESS}`}
        pattern={regexPatterns[ADDRESS.PRIMARY_ADDRESS]}
        ref={register}
        required={required}
        placeholder={t(`myDetails.placeholders.${ADDRESS.PRIMARY_ADDRESS}`)}
      />
      <TextField
        errorHelperMessage={t(
          `myDetails.helperTexts.${ADDRESS.ADDITIONAL_ADDRESS}`
        )}
        fullWidth
        label={t(`myDetails.inputs.${ADDRESS.ADDITIONAL_ADDRESS}`)}
        name={`${prefix}${GUEST_CHECKOUT_ADDRESS.ADDITIONAL_ADDRESS}`}
        pattern={regexPatterns[ADDRESS.ADDITIONAL_ADDRESS]}
        ref={register}
        placeholder={t(`myDetails.placeholders.${ADDRESS.ADDITIONAL_ADDRESS}`)}
      />
      <TextField
        errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.CITY}`)}
        fullWidth
        label={t(`myDetails.inputs.${ADDRESS.CITY}`)}
        name={`${prefix}${GUEST_CHECKOUT_ADDRESS.CITY}`}
        pattern={regexPatterns[ADDRESS.CITY]}
        ref={register}
        required={required}
        placeholder={t(`myDetails.placeholders.${ADDRESS.CITY}`)}
      />
      {isProvinceRelevantForCountry && (
        <TextField
          fullWidth
          label={t(`myDetails.inputs.${ADDRESS.PROVINCE}`)}
          name={`${prefix}${GUEST_CHECKOUT_ADDRESS.PROVINCE}`}
          pattern={regexPatterns[ADDRESS.PROVINCE]}
          ref={register}
          required={required}
          placeholder={t(`myDetails.placeholders.${ADDRESS.PROVINCE}`)}
        />
      )}
      <TextField
        errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.POST_CODE}`)}
        fullWidth
        label={t(`myDetails.inputs.${ADDRESS.POST_CODE}`)}
        name={`${prefix}${POST_CODE}`}
        pattern={postCodePattern}
        ref={register}
        required={required}
        title={t(`myDetails.titles.${ADDRESS.POST_CODE}`)}
        placeholder={t(`myDetails.placeholders.${ADDRESS.POST_CODE}`)}
      />
      {isCountrySelectVisible ? (
        <SelectField
          fullWidth
          label={t(`myDetails.inputs.${ADDRESS.COUNTRY}`)}
          name={COUNTRY_FIELD}
          options={countries}
          ref={register}
          required={required}
        />
      ) : (
        <TextField
          fullWidth
          errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.COUNTRY}`)}
          label={t(`myDetails.inputs.${ADDRESS.COUNTRY}`)}
          name={COUNTRY_FIELD}
          pattern={regexPatterns[ADDRESS.COUNTRY]}
          ref={register}
          required={required}
          placeholder={t(`myDetails.placeholders.${ADDRESS.COUNTRY}`)}
        />
      )}
    </>
  );
};

CustomerCreationFormAddress.propTypes = {
  isProvinceRelevantForCountry: bool,
  prefix: string,
  regexPatterns: object,
  register: any,
  required: bool,
  watch: func,
};
